import {CodeIcon, ComposeIcon, EyeIcon} from '@primer/octicons-react'
import {SegmentedControl} from '@primer/react'
import {clsx} from 'clsx'

import type {MarkdownViewMode} from '../types'
import styles from './ViewSwitch.module.css'

export type MarkdownDefaultViewMode = 'edit' | 'hybrid'

type ViewSwitchProps = {
  selectedView: MarkdownViewMode
  onViewSelect?: (view: MarkdownViewMode) => void
  disabled?: boolean
  /** Called when the preview should be loaded. */
  onLoadPreview?: () => void
}

// no point in memoizing this component because onLoadPreview depends on value, so it would still re-render on every change
export const ViewSwitch = ({
  selectedView,
  onViewSelect: externalOnViewSelect,
  onLoadPreview,
  disabled,
}: ViewSwitchProps) => {
  // don't get disabled from context - the switch is not disabled when the editor is disabled

  const onViewSelect = (mode: MarkdownViewMode) => {
    externalOnViewSelect?.(mode)
  }

  return (
    <nav>
      <SegmentedControl aria-label="View mode">
        <SegmentedControl.IconButton
          className={clsx(styles.segmentedControl, selectedView === 'edit' && styles.active)}
          selected={selectedView === 'edit'}
          value="edit"
          aria-label="Edit mode"
          disabled={disabled}
          icon={<CodeIcon />}
          onClick={() => onViewSelect?.('edit')}
        />
        <SegmentedControl.IconButton
          className={clsx(styles.segmentedControl, selectedView === 'preview' && styles.active)}
          selected={selectedView === 'preview'}
          value="preview"
          aria-label="Preview mode"
          disabled={disabled}
          icon={<EyeIcon />}
          onClick={() => onViewSelect?.('preview')}
          onMouseOver={() => onLoadPreview?.()}
          onFocus={() => onLoadPreview?.()}
        />
        <SegmentedControl.IconButton
          className={clsx(styles.segmentedControl, selectedView === 'hybrid' && styles.active)}
          value="hybrid"
          selected={selectedView === 'hybrid'}
          aria-label="Hybrid mode"
          disabled={disabled}
          icon={<ComposeIcon />}
          onClick={() => onViewSelect?.('hybrid')}
        />
      </SegmentedControl>
    </nav>
  )
}

try{ ViewSwitch.displayName ||= 'ViewSwitch' } catch {}